import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { Grid } from '@mui/material'
import { useSnackbar } from 'notistack'

import BreadcrumbsComponent from '../../components/breadcrumbs'
import Section from '../../components/section'
import { AppContext, useHost } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useDocumentTitle from '../../utils/hooks/useDocumentTitle'
import useSharedStyles from '../../utils/shared.styles'

import DeviceActions from './components/actions'
import DeviceAlertsTable from './components/alerts-table'
import DeviceChannelsTable from './components/channels-table'
import DeviceCharts from './components/charts'
import DeviceDetails from './components/details'
import DeviceDetailsLoading from './components/details-loading'
import FilterComponent, { defaultAlertFilters } from './components/filter'
import DeviceHeader from './components/header'
import SearchComponent from './components/search'
import TabsComponent from './components/tabs'
import DeviceContext from './context'

const TabPanel = ({ children, value, tab }: { children?: React.ReactNode; value: TabsListType; tab: TabsListType }) => (
  <div role="tabpanel" hidden={value !== tab}>
    {value === tab && children}
  </div>
)

const Devices = () => {
  const { deviceId } = useParams()
  const [searchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as TabsListType | null) || 'alerts'

  const { enqueueSnackbar } = useSnackbar()
  const { pageRefreshCounter } = useContext(AppContext)
  const { auth, hasInternalUserAccess } = useAuthentication()
  const host = useHost()

  const [device, setDevice] = useState<IDeviceAPIObject>()
  const [charts, setCharts] = useState<{ [x: string]: { data: IChartData[]; includesAll: boolean } }>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [searchText, setSearchText] = useState<string>('')
  const [alertFilters, setAlertFilters] = useState<DeviceAlertFilterForm>({ ...defaultAlertFilters })
  const [devicePhotoRefreshCounter, setDevicePhotoRefreshCounter] = useState<number>(0)
  const sharedClasses = useSharedStyles

  useDocumentTitle(deviceId && device ? `${device.vanity} & ${device.folderName} | Meshify Central` : 'Meshify Central')

  const fetchDevice = async () => {
    if (!deviceId || !host || !auth.user?.access_token) return
    const res = await Api.get(`${host}/api/v2/central/devices/${deviceId}`)
    if (res.ok) {
      setDevice(res.body)
    } else {
      enqueueSnackbar('Something went wrong and we could not load the device', { variant: 'error' })
    }
    setIsLoading(false)
  }

  const fetchInitialDevice = useEffectEvent(fetchDevice)
  useEffect(() => {
    fetchInitialDevice()
  }, [deviceId, host, pageRefreshCounter])

  const showLoading = isLoading || !device

  return (
    <DeviceContext.Provider value={{ charts, setCharts, devicePhotoRefreshCounter, setDevicePhotoRefreshCounter }}>
      <div>
        <BreadcrumbsComponent folderId={device?.folderId} />

        {showLoading ? (
          <DeviceDetailsLoading />
        ) : (
          <>
            <DeviceHeader device={device} />

            <Section
              component={<DeviceDetails device={device} setDevice={setDevice} />}
              title="Device details"
              showEdit={false}
              actionButton={hasInternalUserAccess ? <DeviceActions device={device} setDevice={setDevice} fetchDevice={fetchDevice} /> : undefined}
            />

            <Grid container={true} direction="row" sx={[sharedClasses.centerContentVertical, { justifyContent: 'space-between' }]}>
              <Grid size={4}>
                <TabsComponent />
              </Grid>

              <Grid
                container={true}
                size="auto"
                sx={{
                  gap: 1,
                }}
              >
                {tab !== 'charts' && (
                  <Grid>
                    <SearchComponent searchText={searchText} setSearchText={setSearchText} />
                  </Grid>
                )}

                {tab === 'alerts' && (
                  <Grid>
                    <FilterComponent setAlertFilters={setAlertFilters} />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <TabPanel tab="alerts" value={tab}>
              <DeviceAlertsTable device={device} searchText={searchText} filters={alertFilters} />
            </TabPanel>

            <TabPanel tab="charts" value={tab}>
              <DeviceCharts
                deviceId={device.id}
                currentValues={device.currentValues}
                createdAt={device.createdAt}
                alerts={device.alerts}
                nodeType={device.nodeType.name}
              />
            </TabPanel>

            <TabPanel tab="channels" value={tab}>
              <DeviceChannelsTable device={device} searchText={searchText} />
            </TabPanel>
          </>
        )}
      </div>
    </DeviceContext.Provider>
  )
}

export default Devices
